import type { ReactElement } from 'react';

/** A tiling pixel-art pattern (rows of '.'/'1'..'9' indexing colors). */
export interface PixelPatternDef {
  readonly colors: readonly string[];
  readonly rows: readonly string[];
}

export interface PixelPatternProps {
  pattern: PixelPatternDef;
  /** Document-unique id for the <pattern> def (derive from the host testId). */
  id: string;
  /** Pixel size of one tile cell. */
  cell?: number;
  testId?: string;
  className?: string;
}

/**
 * Fills its box with a seamlessly repeating pixel tile via an SVG <pattern>.
 * Used as a low-opacity decorative background behind card content.
 */
export function PixelPattern({ pattern, id, cell = 9, testId, className }: PixelPatternProps): ReactElement {
  const height = pattern.rows.length;
  const width = pattern.rows[0]?.length ?? 0;
  return (
    <svg
      width="100%"
      height="100%"
      preserveAspectRatio="none"
      shapeRendering="crispEdges"
      aria-hidden="true"
      focusable="false"
      className={className}
      {...(testId === undefined ? {} : { 'data-testid': testId })}
    >
      <defs>
        <pattern id={id} patternUnits="userSpaceOnUse" width={width * cell} height={height * cell}>
          {pattern.rows.flatMap((row, y) => [...row].map((pixel, x) => {
            if (pixel === '.') return null;
            const fill = pattern.colors[Number(pixel) - 1];
            return fill === undefined
              ? null
              : <rect key={`${x}:${y}`} x={x * cell} y={y * cell} width={cell} height={cell} fill={fill} />;
          }))}
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}

/**
 * The same tile as a CSS background (`url("data:…")`, `cell` px per pixel), for
 * a surface too small to host its own <svg> — the face-down orb of the plan.
 */
export function pixelPatternDataUri(pattern: PixelPatternDef, cell: number): string {
  const height = pattern.rows.length;
  const width = pattern.rows[0]?.length ?? 0;
  const rects = pattern.rows.flatMap((row, y) => [...row].map((pixel, x) => {
    const fill = pixel === '.' ? undefined : pattern.colors[Number(pixel) - 1];
    return fill === undefined
      ? ''
      : `<rect x='${x * cell}' y='${y * cell}' width='${cell}' height='${cell}' fill='${fill}'/>`;
  }));
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${width * cell}' height='${height * cell}' shape-rendering='crispEdges'>${rects.join('')}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
